import React, { useState, useEffect, useContext } from "react";
import { getCompanySettings, updateCompanySettings } from "../services/api_settings";
import { AuthContext } from "../contexts/auth";
import Swal from "sweetalert2";
import { estraiMessaggiErrore } from "../services/utils";

const emailSettingsConfig = [
  { key: "company_email_enabled", label: "Abilita ricezione email (turni / giustificativi / rimborsi spese)" },
  { key: "employee_can_view_previous_shifts", label: "I dipendenti possono visualizzare i turni dei mesi precedenti a quello in corso" },
  { key: "employee_can_view_previous_inouts", label: "I dipendenti possono visualizzare le timbrature dei mesi precedenti a quello in corso" },
  { key: "allow_hidden_breaks", label: "Abilita riduzione manuale turno" },
  { key: "inout_on_break", label: "Abilita controllo della timbratura sulla pausa della fascia oraria" },
  { key: "payroll_ai_overwrite_existing", label: "Sovrascrivi dati anagrafici, contrattuali e documentali da lettura busta tramite AI" },
];

// DOCS
const DOCS_DAYS_KEY = "company_docs_email_days_before_default";
const DOCS_SEND_COMPANY_KEY = "company_docs_email_send_to_company";
const DOCS_SEND_HANDLERS_KEY = "company_docs_email_send_to_handlers";
const DOCS_OTHERS_KEY = "company_docs_email_send_to_others_emails";

// CONTRACTS
const END_CONTRACTS_DAYS_KEY = "company_end_contracts_email_days_before_default";
const END_CONTRACTS_SEND_COMPANY_KEY = "company_end_contracts_email_send_to_company";
const END_CONTRACTS_SEND_HANDLERS_KEY = "company_end_contracts_email_send_to_handlers";
const END_CONTRACTS_OTHERS_KEY = "company_end_contracts_email_send_to_others_emails";

const clampDays = (v) => {
  if (v === null) return null;
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return 7;
  return Math.min(31, Math.max(1, n));
};

const normalizeDaysForCompare = (v) => {
  const s = String(v ?? "").trim();
  if (s === "") return null;
  return clampDays(s);
};

const normalizeEmailsCsv = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .join(", ");
};


const CompanyEmailSetting = () => {
  const { token, profile } = useContext(AuthContext);
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Stato "draft" per i due reminder (così non salvi finché non confermi)
  const [docsDaysDraft, setDocsDaysDraft] = useState("");
  const [contractsDaysDraft, setContractsDaysDraft] = useState("");

  const [docsOthersDraft, setDocsOthersDraft] = useState("");
  const [contractsOthersDraft, setContractsOthersDraft] = useState("");
  
  const levelValues = Object.values(profile?.level || {}).flat();
  const hasShiftsLevel = levelValues.includes("shifts");
  const hasAiBustePackage = levelValues.includes("payrolls_ai") 
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const fetched = await getCompanySettings(token, "payrolls");

        const updated = {};
        emailSettingsConfig.forEach(({ key }) => {
          updated[key] = fetched?.[key] ?? false;
        });

        updated[DOCS_DAYS_KEY] = fetched?.[DOCS_DAYS_KEY] === undefined ? 7 : fetched?.[DOCS_DAYS_KEY];
        updated[DOCS_SEND_COMPANY_KEY] = fetched?.[DOCS_SEND_COMPANY_KEY] ?? true;
        updated[DOCS_SEND_HANDLERS_KEY] = fetched?.[DOCS_SEND_HANDLERS_KEY] ?? true;
        updated[DOCS_OTHERS_KEY] = fetched?.[DOCS_OTHERS_KEY] ?? "";

        updated[END_CONTRACTS_DAYS_KEY] = fetched?.[END_CONTRACTS_DAYS_KEY] === undefined ? 7 : fetched?.[END_CONTRACTS_DAYS_KEY];
        updated[END_CONTRACTS_SEND_COMPANY_KEY] = fetched?.[END_CONTRACTS_SEND_COMPANY_KEY] ?? true;
        updated[END_CONTRACTS_SEND_HANDLERS_KEY] = fetched?.[END_CONTRACTS_SEND_HANDLERS_KEY] ?? true;
        updated[END_CONTRACTS_OTHERS_KEY] = fetched?.[END_CONTRACTS_OTHERS_KEY] ?? "";

        setSettings(updated);
        setInitialSettings(updated);

        // inizializza draft dai valori correnti (null => "")
        setDocsDaysDraft(updated[DOCS_DAYS_KEY] ?? "");
        setContractsDaysDraft(updated[END_CONTRACTS_DAYS_KEY] ?? "");
        setDocsOthersDraft(normalizeEmailsCsv(updated[DOCS_OTHERS_KEY] ?? ""));
        setContractsOthersDraft(normalizeEmailsCsv(updated[END_CONTRACTS_OTHERS_KEY] ?? ""));
      } catch (err) {
        console.error("Errore durante il caricamento delle impostazioni.", err);
        Swal.fire({ icon: "error", title: "Errore", text: "Impossibile caricare le impostazioni." });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [token]);

  const handleCheckboxChange = async (key) => {
    const currentValue = Boolean(settings[key]);
    const newValue = !currentValue;

    // Conferma SOLO se stai passando da true -> false su allow_hidden_breaks
    if (key === "allow_hidden_breaks" && currentValue === true && newValue === false) {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "Confermi la disattivazione?",
        html: "Stai disabilitando la riduzione dell'orario di lavoro. Eventuali pause create in modalità 'non visibile' verranno rese visibili. Confermi?",
        showCancelButton: true,
        confirmButtonText: "Sì, disabilita",
        cancelButtonText: "Annulla",
        reverseButtons: true,
        focusCancel: true,
      });

      if (!isConfirmed) return;
    }

    try {
      await updateCompanySettings(token, { [key]: newValue });

      setSettings((prev) => ({ ...prev, [key]: newValue }));
      setInitialSettings((prev) => ({ ...prev, [key]: newValue }));

      Swal.fire({ icon: "success", title: "Salvato!", showConfirmButton: false, timer: 800 });
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: estraiMessaggiErrore(error),
        confirmButtonText: "OK",
      });
    }
  };

  const saveDays = async (daysKey, draftValue, setDraftValue) => {
    const payloadVal = normalizeDaysForCompare(draftValue); // "" => null, "7" => 7, clamp 1..31

    try {
      await updateCompanySettings(token, { [daysKey]: payloadVal });

      setSettings((prev) => ({ ...prev, [daysKey]: payloadVal }));
      setInitialSettings((prev) => ({ ...prev, [daysKey]: payloadVal }));

      // riallinea il draft al valore salvato (null => "")
      setDraftValue(payloadVal ?? "");

      Swal.fire({ icon: "success", title: "Salvato!", showConfirmButton: false, timer: 800 });
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: estraiMessaggiErrore(error),
        confirmButtonText: "OK",
      });
    }
  };

  const saveOthers = async (othersKey, draftValue, setDraftValue) => {
    const payloadVal = normalizeEmailsCsv(draftValue); // stringa

    try {
      await updateCompanySettings(token, { [othersKey]: payloadVal });

      setSettings((prev) => ({ ...prev, [othersKey]: payloadVal }));
      setInitialSettings((prev) => ({ ...prev, [othersKey]: payloadVal }));

      setDraftValue(payloadVal);

      Swal.fire({ icon: "success", title: "Salvato!", showConfirmButton: false, timer: 800 });
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: estraiMessaggiErrore(error),
        confirmButtonText: "OK",
      });
    }
  };


  return (
    <div className="max-w-full px-4 mx-auto">
      {/* Toggle generali visibili solo con shifts */}
      {hasShiftsLevel && emailSettingsConfig
        .filter(({ key }) => key !== "payroll_ai_overwrite_existing" || hasAiBustePackage)
        .map(({ key, label }) => (
          <div key={key} className="flex justify-between items-center py-2 border-b border-gray-200">
            <div className="text-md text-gray-900">{label}</div>
            <div>
              {isLoading ? (
                "Caricamento..."
              ) : (
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded text-indigo-600 focus:ring-indigo-600"
                  checked={settings[key] || false}
                  onChange={() => handleCheckboxChange(key)}
                />
              )}
            </div>
          </div>
        ))
      }

      <ReminderBlock
        title="Promemoria scadenza documenti"
        subtitle="Imposta i giorni e i destinatari di default per la ricezione del promemoria di scadenza documenti. Le impostazioni potranno essere modificate anche singolarmente sul documento."
        secondSubtitle="Disabilita l'impostazione per applicare la personalizzazione su ogni scadenza."
        daysKey={DOCS_DAYS_KEY}
        daysDraft={docsDaysDraft}
        setDaysDraft={setDocsDaysDraft}
        sendCompanyKey={DOCS_SEND_COMPANY_KEY}
        sendHandlersKey={DOCS_SEND_HANDLERS_KEY}
        othersKey={DOCS_OTHERS_KEY}
        othersDraft={docsOthersDraft}
        setOthersDraft={setDocsOthersDraft}
        saveOthers={saveOthers}  
        settings={settings}
        initialSettings={initialSettings}
        isLoading={isLoading}
        normalizeDaysForCompare={normalizeDaysForCompare}
        saveDays={saveDays}
        handleCheckboxChange={handleCheckboxChange}
        hasShiftsLevel={hasShiftsLevel}
      />

      <hr className="mt-4 mb-2 border-gray-200" />

      <ReminderBlock
        title="Promemoria scadenza contratti"
        subtitle="Imposta i giorni e i destinatari di default per la ricezione del promemoria di scadenza contratti. Le impostazioni potranno essere modificate anche singolarmente sul contratto."
        secondSubtitle="Disabilita l'impostazione per applicare la personalizzazione su ogni scadenza."
        daysKey={END_CONTRACTS_DAYS_KEY}
        daysDraft={contractsDaysDraft}
        setDaysDraft={setContractsDaysDraft}
        sendCompanyKey={END_CONTRACTS_SEND_COMPANY_KEY}
        sendHandlersKey={END_CONTRACTS_SEND_HANDLERS_KEY}
        othersKey={END_CONTRACTS_OTHERS_KEY}
        othersDraft={contractsOthersDraft}
        setOthersDraft={setContractsOthersDraft}
        saveOthers={saveOthers}  
        settings={settings}
        initialSettings={initialSettings}
        isLoading={isLoading}
        normalizeDaysForCompare={normalizeDaysForCompare}
        saveDays={saveDays}
        handleCheckboxChange={handleCheckboxChange}
        hasShiftsLevel={hasShiftsLevel}
      />
    </div>
  );
};

export default CompanyEmailSetting;


const ReminderBlock = ({
  title,
  subtitle,
  secondSubtitle,
  daysKey,
  daysDraft,
  setDaysDraft,
  sendCompanyKey,
  sendHandlersKey,
  othersKey,
  othersDraft,
  setOthersDraft,
  saveOthers,
  settings,
  initialSettings,
  isLoading,
  normalizeDaysForCompare,
  saveDays,
  handleCheckboxChange,
  hasShiftsLevel,
}) => {
  const enabled = settings[daysKey] !== null && settings[daysKey] !== undefined;

  const current = normalizeDaysForCompare(daysDraft);
  const initial = normalizeDaysForCompare(initialSettings[daysKey]);
  const currentOthers = normalizeEmailsCsv(othersDraft);
  const initialOthers = normalizeEmailsCsv(initialSettings[othersKey]);
  
  const othersDirty = currentOthers !== initialOthers;
  const isDirty = current !== initial;

  const canSaveOthers = othersDirty && !isLoading;
  const canSave = isDirty && !isLoading;

  return (
    <div>
      <div className="text-md text-gray-900 mt-2">{title}</div>
      <div className="mt-2 space-y-3 w-full border border-gray-200 rounded p-3 bg-gray-50">
        {subtitle && <div className="text-xs  text-gray-800">{subtitle} <br/> <span>
          {secondSubtitle && <span className="text-xs  text-gray-800">{secondSubtitle}</span>}
          </span> </div>}
      
        <div className="flex items-start justify-between gap-4">
          <div className="text-sm text-gray-800">
            Giorni prima della scadenza
            <div className="text-xs text-gray-500">Valore da 1 a 31. Svuota il campo e conferma per disabilitare.</div>
          </div>

          <div className="flex items-center gap-2">
            {isDirty && (
            <button
              type="button"
              className={`px-3 py-1 rounded text-sm font-semibold transition ${
                canSave
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              } ${isDirty ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              onClick={() => saveDays(daysKey, daysDraft, setDaysDraft)}
              disabled={!canSave}
              title="Salva il valore inserito"
            >
              Conferma
            </button>
            )}
            <input
              type="number"
              min={1}
              max={31}
              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={daysDraft ?? ""}
              onChange={(e) => setDaysDraft(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className={`space-y-2 ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-800">Invia all’azienda</div>
            <input
              type="checkbox"
              className="h-4 w-4 rounded text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 "
              checked={settings[sendCompanyKey] || false}
              onChange={() => handleCheckboxChange(sendCompanyKey)}
              disabled={isLoading}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className={`text-sm ${hasShiftsLevel ? "text-gray-800" : "text-gray-400"}`}>
              Invia ai gestori
              {!hasShiftsLevel && (
                <span className="ml-2 text-xs font-semibold text-indigo-500">
                  Disponibile con HR Pro
                </span>
              )}
            </div>

            <input
              type="checkbox"
              className={`h-4 w-4 rounded ${
                hasShiftsLevel
                  ? "text-indigo-600 focus:ring-indigo-600"
                  : "text-gray-300 cursor-not-allowed"
              }`}
              checked={hasShiftsLevel ? Boolean(settings[sendHandlersKey]) : false}
              onChange={() => {
                if (!hasShiftsLevel) return;
                handleCheckboxChange(sendHandlersKey);
              }}
              disabled={isLoading || !hasShiftsLevel}
            />
          </div>

          <div className="pt-2">
            <div className="text-sm text-gray-800">Altri destinatari (email)</div>
            <div className="text-xs text-gray-500">Inserisci uno o più indirizzi separati da virgola.</div>

            <div className="flex items-start gap-2 mt-1">
              <textarea
                className="
                  w-full min-h-[40px]
                  border border-gray-300 rounded
                  px-3 py-2
                  text-sm
                  text-gray-800
                  tracking-wide
                  decoration-gray-400
                  placeholder:text-gray-400
                  focus:outline-none
                  focus:ring-2
                  focus:ring-indigo-500"
                value={othersDraft ?? ""}
                onChange={(e) => setOthersDraft(e.target.value)}
                disabled={isLoading}
                placeholder="es. scadenze@azienda.it, consulente@studio.it"
              />

              {othersDirty && (
                <button
                  type="button"
                  className={`px-3 py-1 rounded text-sm font-semibold transition ${
                    canSaveOthers
                      ? "bg-indigo-600 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      : "bg-gray-300 text-gray-600 cursor-not-allowed"
                  }`}
                  onClick={() => saveOthers(othersKey, othersDraft, setOthersDraft)}
                  disabled={!canSaveOthers}
                  title="Salva gli indirizzi inseriti"
                >
                  Conferma
                </button>
              )}
            </div>
          </div>
        </div>

        {!enabled && <div className="text-sm text-indigo-700">Promemoria disabilitato</div>}
      </div>
    </div>
  );
};
